import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './Navbar.module.scss';
import { routes } from 'routing/routes';
import { DiscordIcon } from 'icons/discord';
import { TwitterIcon } from 'icons/twitter';
import { DropdownNavMenu, NavMenuItem, NavbarItem, AccordionNavMenu } from './NavbarMenu';
import { externalRoutes } from 'routing/externalRoutes';
import { useUser } from 'hooks/useUser';
import { ProfileTooltip } from './ProfileTooltip';
import { DuskBreakersLogo } from 'icons/duskbreakers';
import cls from 'classnames';

import aboutImg from '../../assets/img/navbar/about.jpg';
import galleryImg from '../../assets/img/navbar/gallery.jpg';
import playNowImg from '../../assets/img/navbar/play-now.jpg';
import resourcesImg from '../../assets/img/navbar/resources.jpg';
import storyImg from '../../assets/img/navbar/story.jpg';
import { Socials } from 'components/Socials/Socials';
import { HorizontalDivider } from 'components/HorizontalDivider/HorizontalDivider';
import { SiteMap } from 'routing/siteMap';
import { HambugerIcon, Union } from 'components/Icons/Icons';

type NavItem =
  | {
      name: string;
      type: 'Link';
      prop: string;
    }
  | {
      name: string;
      type: 'Menu';
      prop: NavMenuItem;
    };

export const navItems: NavItem[] = [
  {
    name: 'Mint',
    type: 'Link',
    prop: routes.mint(),
  },
  {
    name: SiteMap.About.sectionName,
    type: 'Menu',
    prop: {
      title: 'Learn About Us',
      description:
        'DuskBreakers is a sci-fi inspired metaverse and gaming experience powered by blockchain. \n\nJoin a select squad of Breakers who spend their days exploring a mysterious alien spaceship filled with friends, foes, and otherworldly technology.',
      img: aboutImg,
      links: SiteMap.About.links,
    },
  },
  {
    name: SiteMap.Story.sectionName,
    type: 'Menu',
    prop: {
      title: 'Explore the Story',
      description:
        'From the Dusk and her alien creatures to the city of Machafuko and the heroic Breakers who will change our fate, we welcome you to explore the world of DuskBreakers.',
      img: storyImg,
      links: SiteMap.Story.links,
    },
  },
  {
    name: SiteMap.Gallery.sectionName,
    type: 'Menu',
    prop: {
      title: 'Gallery',
      description:
        'DuskBreakers is a sci-fi inspired metaverse and gaming experience powered by blockchain. \n\nJoin a select squad of Breakers who spend their days exploring a mysterious alien spaceship filled with friends, foes, and otherworldly technology.',
      img: galleryImg,
      links: SiteMap.Gallery.links,
    },
  },
  {
    name: SiteMap.Resources.sectionName,
    type: 'Menu',
    prop: {
      title: 'Resources',
      description:
        'DuskBreakers is a sci-fi inspired metaverse and gaming experience powered by blockchain. \n\nJoin a select squad of Breakers who spend their days exploring a mysterious alien spaceship filled with friends, foes, and otherworldly technology.',
      img: resourcesImg,
      links: SiteMap.Resources.links,
    },
  },
  {
    name: SiteMap.Play.sectionName,
    type: 'Menu',
    prop: {
      title: 'Always Free to Play',
      description:
        'Try one of our minigame experiences on our Windows Launcher or right here in your browser! \n\nYou do not need a DuskBreakers NFT to play, but you will be asked to connect a wallet and create a Breaker ID login to begin. Can you top the leaderboards?',
      img: playNowImg,
      links: SiteMap.Play.links,
    },
  },
];

export function Navbar() {
  const { ethereum } = useUser();
  const isWalletConnected = ethereum.isConnected;

  const [isScrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const position = window.scrollY;

      if (position > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [setScrolled]);

  return (
    <>
      <DesktopNavbar scrolled={isScrolled} isWalletConnected={isWalletConnected} />
      <MobileNavbar scrolled={isScrolled} isWalletConnected={isWalletConnected} />
    </>
  );
}

type NavbarProps = {
  scrolled: boolean;
  isWalletConnected: boolean;
};

const DesktopNavbar = ({ scrolled, isWalletConnected }: NavbarProps) => {
  return (
    <nav className={scrolled ? cls(styles.navbar, styles.navbarScolled) : styles.navbar}>
      <Link id="topbar-logo" to={routes.home()}>
        <DuskBreakersLogo />
      </Link>
      <ul className={styles.navItemList}>
        {navItems.map((item) => {
          return (
            <div key={item.name}>
              {item.type === 'Link' ? (
                <NavbarItem name={item.name} path={item.prop} />
              ) : (
                <DropdownNavMenu
                  name={item.name}
                  title={item.prop.title}
                  description={item.prop.description}
                  img={item.prop.img}
                  links={item.prop.links}
                />
              )}
            </div>
          );
        })}
      </ul>
      <div className={styles.profileSection}>
        <a href={externalRoutes.discord()} target="_blank" rel="noreferrer">
          <DiscordIcon />
        </a>
        <a href={externalRoutes.twitter()} target="_blank" rel="noreferrer">
          <TwitterIcon stroke={styles.iconColor} />
        </a>
        {isWalletConnected ? (
          <ProfileTooltip />
        ) : (
          <Link to={routes.login()}>
            <button className="btn btn-sm btn-primary">Connect Wallet</button>
          </Link>
        )}
      </div>
    </nav>
  );
};

const MobileNavbar = ({ scrolled, isWalletConnected }: NavbarProps) => {
  const [isMenuOpen, setMenuOpen] = useState(false);

  return (
    <>
      <div
        className={scrolled ? cls(styles.navbarMobile, styles.navbarScolled) : styles.navbarMobile}
      >
        <Link id="topbar-logo" to={routes.home()}>
          <DuskBreakersLogo />
        </Link>
        <div className={styles.navOptions}>
          <ProfileTooltip />
          <button className={styles.menuButton} onClick={() => setMenuOpen(!isMenuOpen)}>
            <HambugerIcon />
          </button>
        </div>
      </div>
      {isMenuOpen && (
        <div className={styles.navMobileBackground}>
          <div className={styles.navMobileMenu}>
            <div>
              <div className={styles.mobileMenuHeader}>
                <span>Main Menu</span>
                <button className={styles.menuButton} onClick={() => setMenuOpen(!isMenuOpen)}>
                  <Union />
                </button>
              </div>
              <HorizontalDivider className={styles.navMobileDivider} />
              <div>
                <>
                  {navItems.map((item, i) => {
                    return (
                      <div key={i}>
                        {item.type === 'Link' && <NavbarItem name={item.name} path={item.prop} />}
                        {item.type === 'Menu' && (
                          <>
                            <HorizontalDivider className={styles.navMobileDivider} />
                            <AccordionNavMenu
                              onClickItem={() => setMenuOpen(false)}
                              name={item.name}
                              title={item.prop.title}
                              description={item.prop.description}
                              img={item.prop.img}
                              links={item.prop.links}
                            />
                          </>
                        )}
                      </div>
                    );
                  })}
                </>
                {!isWalletConnected && (
                  <Link to={routes.login()}>
                    <button
                      className={cls('btn btn-sm btn-primary', styles.button)}
                      onClick={() => setMenuOpen(false)}
                    >
                      Connect Wallet
                    </button>
                  </Link>
                )}
              </div>
            </div>
            <Socials />
          </div>
        </div>
      )}
    </>
  );
};
